
"use client";

import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { motion } from "framer-motion";
// import Turnstile from "react-turnstile"; // This was commented out
import { toast } from "react-hot-toast";
import { AtSign, Phone, KeyRound } from "lucide-react";

import { GoogleLoginButton } from "@/components/GoogleButton";
import { Spinner, FADE_IN_VARIANTS, buttonClasses, inputBaseClasses, inputErrorClasses, inputDefaultClasses } from "./ui";
import { emailRegex, FormData } from "./schemas";
import { toEnglishDigits, toPersianDigits } from "@/utils/persian-helpers";

type RegistrationDetailsStepProps = {
    onFormSubmit: (data: FormData, turnstileToken: string) => void;
    isLoading: boolean;
    isGoogleLoading: boolean;
    setIsGoogleLoading: (loading: boolean) => void;
};

export const RegistrationDetailsStep: React.FC<RegistrationDetailsStepProps> = ({ onFormSubmit, isLoading, setIsGoogleLoading, isGoogleLoading }) => {
    const { control, handleSubmit, watch, formState: { errors } } = useFormContext<FormData>();
    const identifierValue = watch("identifier");
    // const [turnstileToken, setTurnstileToken] = useState<string | null>(null); // This was commented out

    const handleFormSubmit = (data: FormData) => {
        // Captcha token check has been removed
        onFormSubmit(data, "dev-placeholder-token"); // ✅ A temporary token is sent for development
    };

    return (
        <motion.div key="details-step" variants={FADE_IN_VARIANTS} initial="hidden" animate="visible" exit="exit">
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                <Controller
                    name="identifier"
                    control={control}
                    render={({ field }) => {
                        const isEmail = emailRegex.test(field.value || "");
                        const displayValue = isEmail ? field.value : toPersianDigits(field.value);

                        return (
                            <div>
                                <label htmlFor={field.name} className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">ایمیل یا شماره تلفن</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        {isEmail ? <AtSign className="h-5 w-5 text-zinc-400" /> : <Phone className="h-5 w-5 text-zinc-400" />}
                                    </span>
                                    <input
                                        id={field.name}
                                        {...field}
                                        value={displayValue}
                                        onChange={(e) => {
                                            const englishValue = toEnglishDigits(e.target.value);
                                            field.onChange(englishValue);
                                        }}
                                        placeholder="آدرس ایمیل یا شماره موبایل"
                                        className={`${inputBaseClasses} ${errors.identifier ? inputErrorClasses : inputDefaultClasses}`}
                                    />
                                </div>
                                {errors.identifier && <p className="mt-1 text-xs text-red-600">{errors.identifier.message}</p>}
                            </div>
                        );
                    }}
                />
                <Controller name="password" control={control} render={({ field }) => (
                    <div>
                        <label htmlFor={field.name} className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">رمز عبور</label>
                        <div className="relative">
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"><KeyRound className="h-5 w-5 text-zinc-400" /></span>
                            <input type="password" id={field.name} {...field} placeholder="حداقل ۸ کاراکتر" className={`${inputBaseClasses} ${errors.password ? inputErrorClasses : inputDefaultClasses}`} />
                        </div>
                        {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
                    </div>
                )} />
                {/* Turnstile component was commented out
                <Turnstile
                    sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                    onVerify={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => toast.error("Error loading captcha. Please refresh the page.")}
                    theme="light"
                    action="register"
                />
                */}
                {/* The button is no longer dependent on the captcha token */}
                <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={isLoading} className={buttonClasses}>
                    {isLoading ? <Spinner /> : null}
                    {isLoading ? "در حال ارسال..." : 'ادامه و دریافت کد'}
                </motion.button>
                <GoogleLoginButton setIsGoogleLoading={setIsGoogleLoading} isGoogleLoading={isGoogleLoading}/>
            </form>
        </motion.div>
    );
};
